import {
  Alert,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Icons,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetTracingContactQueryKey,
  useGetTracingContactContact,
  useSetTracingAssessmentWaitingPeriod,
} from '../../../../api/generated/contact-tracing-controller/contact-tracing-controller.ts';
import { TracingContactDetailsDto, TracingContactDetailsDtoAssessmentStatus } from '../../../../api/generated/model';
import { queryClient } from '../../../../api/queryClient.ts';
import { FormActionButtons } from '../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../components/form/GermanDateInput.tsx';
import {
  addDateWeeks,
  formatDateForInput,
  formatDateWithGermanMonth,
  getCurrentDate,
  getDateAfterInputValue,
} from '../../../../utils/date.ts';

const TRACING_WAITING_PERIOD_WEEKS = 8;

interface FormValues {
  incubationPeriodEnd: string;
}

export function WaitingPeriodConfirmationDialog({
  open,
  onClose,
  patientId,
  tracingContactDetails,
}: {
  open: boolean;
  onClose: () => void;
  patientId: string;
  tracingContactDetails: TracingContactDetailsDto;
}) {
  const { data: tracingContactContact } = useGetTracingContactContact(patientId);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      incubationPeriodEnd: formatDateForInput(addDateWeeks(TRACING_WAITING_PERIOD_WEEKS, getCurrentDate())),
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const startDate = tracingContactContact?.contactPatientLastContact ?? getCurrentDate();
  const incubationPeriodEndMinDate = getDateAfterInputValue(formatDateForInput(startDate));

  const isWaitingPeriodActive =
    tracingContactDetails.assessmentStatus === TracingContactDetailsDtoAssessmentStatus.WAITING_PERIOD;

  const setAssessmentWaitingPeriodMutation = useSetTracingAssessmentWaitingPeriod({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetTracingContactQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => toast.error('Beim Setzen der Wartefrist ist ein Fehler aufgetreten'),
    },
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 600 }}>
        <ModalClose />
        <Typography level={'h4'}>
          Inkubationszeit aktivieren für {tracingContactDetails.firstName} {tracingContactDetails.lastName}
        </Typography>
        <Divider />
        {isWaitingPeriodActive && tracingContactDetails.incubationPeriodEnd && (
          <Alert startDecorator={<Icons.InfoOutlined />}>
            Wartefrist bereits aktiv bis {formatDateWithGermanMonth(tracingContactDetails.incubationPeriodEnd)}
          </Alert>
        )}
        <form
          onSubmit={handleSubmit((data) =>
            setAssessmentWaitingPeriodMutation.mutate({
              id: patientId,
              data: data.incubationPeriodEnd,
            }),
          )}>
          <Stack gap={2}>
            <Stack direction={'row'} gap={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>
                  <Typography>Von</Typography>
                </FormLabel>
                <Typography>{formatDateWithGermanMonth(startDate)}</Typography>
              </FormControl>
              <FormControl sx={{ flex: 1 }} error={!!errors.incubationPeriodEnd}>
                <FormLabel>
                  <Typography>Bis</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="incubationPeriodEnd"
                  rules={{
                    required: true,
                    ...(incubationPeriodEndMinDate
                      ? { min: { value: incubationPeriodEndMinDate, message: 'range' } }
                      : {}),
                  }}
                  min={incubationPeriodEndMinDate}
                />
                {errors.incubationPeriodEnd && (
                  <FormHelperText>
                    {errors.incubationPeriodEnd.type === 'required'
                      ? 'Bis Datum muss ausgefüllt werden'
                      : 'Bis Datum muss nach dem Startdatum liegen'}
                  </FormHelperText>
                )}
              </FormControl>
            </Stack>
            <FormActionButtons onAbortClick={handleOnClose} isLoading={setAssessmentWaitingPeriodMutation.isPending} />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
